import React, { useEffect, useState } from "react";
import { Video, Plus, Clock, Link as LinkIcon, Users, Crown, X, Trash2, Calendar, PlayCircle, Film } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ThumbnailPicker from "../components/ThumbnailPicker";

/** Meeting list for Premium Creators + Admins. Host-only. */
export default function Conferences() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [mine, setMine] = useState([]);
  const [invited, setInvited] = useState([]);
  const [composer, setComposer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    kind: "onboarding",
    is_public: false,
    max_participants: 20,
    allow_recording: false,
    invited_emails: "",
    thumbnail_url: "",
  });

  const canHost = user && (user.role === "creator" || user.role === "premium_creator" || user.role === "admin");
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const [m, i] = await Promise.all([
        canHost ? api.get("/meetings/mine") : Promise.resolve({ data: [] }),
        api.get("/meetings/invited"),
      ]);
      setMine(m.data || []);
      setInvited(i.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.user_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Meeting title required");
    setBusy(true);
    try {
      const emails = form.invited_emails.split(/[,\n\s]+/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/meetings", {
        title: form.title,
        description: form.description,
        kind: form.kind,
        is_public: !!form.is_public && isAdmin,
        max_participants: Math.min(parseInt(form.max_participants || 20, 10), 20),
        allow_recording: !!form.allow_recording,
        invited_emails: emails,
        thumbnail_url: form.thumbnail_url || null,
      });
      toast.success("Meeting created");
      setComposer(false);
      setForm({ title: "", description: "", kind: "onboarding", is_public: false, max_participants: 20, allow_recording: false, invited_emails: "", thumbnail_url: "" });
      nav(`/conference/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const copyInvite = (m) => {
    const url = `${window.location.origin}/conference/join/${m.invite_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied");
  };

  const deleteMeeting = async (m) => {
    if (!window.confirm(`Delete meeting "${m.title}"?`)) return;
    try {
      await api.delete(`/meetings/${m.id}`);
      toast.success("Meeting deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="conferences-page" className="pt-32 pb-24 px-4 sm:px-8 md:px-12 max-w-6xl mx-auto">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Conference Rooms</div>
          <h1 className="font-display font-black text-5xl sm:text-6xl text-white mt-3 tracking-tighter">Board & Onboarding Meetings</h1>
          <p className="text-white/60 mt-2 max-w-2xl">Secure HD video calls for Tap In TV creators, partners, and staff. Waiting room, admit/reject, group chat. Recording-ready.</p>
        </div>
        {canHost && (
          <button
            data-testid="conference-new-btn"
            onClick={() => setComposer(true)}
            className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-5 py-3 rounded-full flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New Meeting
          </button>
        )}
      </div>

      {!canHost && (
        <div className="tv-glass rounded-xl border border-white/10 p-5 mt-8">
          <div className="text-white/85">Hosting meetings is limited to <b className="text-tv-brand">Premium Creators</b> and <b className="text-tv-brand">Admins</b>.</div>
          {!user && <Link to="/login" data-testid="conference-signin-link" className="text-tv-accent underline text-sm mt-2 inline-block">Sign in to join an invited meeting</Link>}
        </div>
      )}

      {/* My meetings */}
      {canHost && (
        <section className="mt-10">
          <h2 className="font-display font-bold text-xl text-white mb-3 flex items-center gap-2"><Crown className="w-4 h-4 text-tv-accent" /> Meetings I host</h2>
          {mine.length === 0 ? (
            <div className="tv-glass rounded-xl border border-white/10 p-6 text-white/60 text-sm">You haven't scheduled any meetings yet. Create one to get started.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {mine.map((m) => (
                <MeetingCard key={m.id} m={m} onCopy={() => copyInvite(m)} onDelete={() => deleteMeeting(m)} mine />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Invited */}
      <section className="mt-10">
        <h2 className="font-display font-bold text-xl text-white mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-tv-brand" /> Invited to me</h2>
        {invited.length === 0 ? (
          <div className="tv-glass rounded-xl border border-white/10 p-6 text-white/60 text-sm">No open invites right now.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {invited.map((m) => (
              <MeetingCard key={m.id} m={m} />
            ))}
          </div>
        )}
      </section>

      {composer && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-[#0B1120] border border-white/10 rounded-2xl p-6 w-full max-w-xl relative" data-testid="conference-composer">
            <button type="button" onClick={() => setComposer(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
            <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">New meeting</div>
            <h3 className="font-display font-black text-3xl text-white tracking-tighter mt-1">Schedule a call</h3>

            <label className="block mt-4">
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Title *</span>
              <input data-testid="conf-form-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent" />
            </label>
            <label className="block mt-3">
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Description</span>
              <textarea data-testid="conf-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent resize-none" />
            </label>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Type</span>
                <select data-testid="conf-form-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full mt-1 bg-[#0B1120] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                  <option value="onboarding">Onboarding</option>
                  <option value="board">Board meeting</option>
                  <option value="team">Team sync</option>
                  <option value="community">Community Q&amp;A</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Max participants (≤20)</span>
                <input data-testid="conf-form-max" type="number" min={2} max={20} value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent" />
              </label>
            </div>
            <label className="block mt-3">
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Invite by email (comma-separated)</span>
              <textarea data-testid="conf-form-invites" value={form.invited_emails} onChange={(e) => setForm({ ...form, invited_emails: e.target.value })} rows={2} placeholder="alice@example.com, bob@example.com" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono outline-none focus:border-tv-accent resize-none" />
            </label>
            <div className="mt-4">
              <ThumbnailPicker
                value={form.thumbnail_url}
                onChange={(url) => setForm({ ...form, thumbnail_url: url })}
                testId="conf-form-thumbnail"
                label="Thumbnail (optional)"
              />
            </div>
            <div className="flex flex-wrap gap-4 mt-4">
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input type="checkbox" data-testid="conf-form-allow-rec" checked={form.allow_recording} onChange={(e) => setForm({ ...form, allow_recording: e.target.checked })} /> Allow recording
              </label>
              {isAdmin && (
                <label className="flex items-center gap-2 text-sm text-white/80">
                  <input type="checkbox" data-testid="conf-form-is-public" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} /> Public — anyone with link can join (admin only)
                </label>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setComposer(false)} className="text-white/70 px-4 py-2 text-sm">Cancel</button>
              <button disabled={busy} data-testid="conf-form-submit" className="bg-tv-accent text-black font-bold px-5 py-2 rounded-full disabled:opacity-60">
                {busy ? "Creating…" : "Create & open"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function MeetingCard({ m, onCopy, onDelete, mine }) {
  const statusColor = m.status === "live" ? "bg-tv-live/20 text-red-300" : m.status === "ended" ? "bg-white/10 text-white/50" : "bg-tv-accent/20 text-yellow-300";
  const icalUrl = `${process.env.REACT_APP_BACKEND_URL}/api/meetings/${m.id}/ical`;
  return (
    <div data-testid={`conference-card-${m.id}`} className="tv-glass rounded-xl border border-white/10 overflow-hidden flex flex-col">
      {m.thumbnail_url ? (
        <div className="aspect-video bg-black">
          <img src={m.thumbnail_url} alt="" className="w-full h-full object-cover" data-testid={`conference-thumb-${m.id}`} />
        </div>
      ) : (
        <div className="aspect-video bg-gradient-to-br from-tv-brand/30 via-[#0B1120] to-tv-accent/20 flex items-center justify-center">
          <Video className="w-10 h-10 text-white/25" />
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${statusColor}`}>{m.status}</span>
          {m.is_public && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-tv-brand/20 text-blue-300">Public</span>}
          {m.allow_recording && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-white/10 text-white/70">REC OK</span>}
        </div>
        <div className="font-display font-bold text-white mt-2 text-lg tracking-tight line-clamp-2">{m.title}</div>
        {m.description && <div className="text-white/60 text-xs mt-1 line-clamp-2">{m.description}</div>}
        <div className="text-white/50 text-xs mt-3 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> ≤{m.max_participants}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {m.kind}</span>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          <Link to={`/conference/${m.id}`} data-testid={`conference-join-${m.id}`} className="bg-tv-accent hover:bg-yellow-400 text-black text-xs font-bold px-4 py-2 rounded-full inline-flex items-center gap-1.5">
            <Video className="w-3 h-3" /> {mine ? "Open room" : "Join"}
          </Link>
          {mine && (
            <>
              <button onClick={onCopy} data-testid={`conference-copy-invite-${m.id}`} className="text-white/80 hover:text-white text-xs font-semibold px-3 py-2 rounded-full bg-white/5 inline-flex items-center gap-1.5">
                <LinkIcon className="w-3 h-3" /> Copy invite
              </button>
              <a href={icalUrl} data-testid={`conference-ical-${m.id}`} className="text-white/80 hover:text-white text-xs font-semibold px-3 py-2 rounded-full bg-white/5 inline-flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> .ics
              </a>
              <button onClick={onDelete} data-testid={`conference-delete-${m.id}`} className="text-red-300 hover:text-red-200 text-xs font-semibold px-3 py-2 rounded-full bg-red-500/10 inline-flex items-center gap-1.5">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </>
          )}
        </div>

        {Array.isArray(m.recordings) && m.recordings.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10" data-testid={`conference-recordings-${m.id}`}>
            <div className="text-[10px] uppercase tracking-wider text-tv-accent font-bold flex items-center gap-1.5">
              <Film className="w-3 h-3" /> Recordings ({m.recordings.length})
            </div>
            <div className="mt-2 space-y-1.5">
              {m.recordings.slice(-5).reverse().map((rec, idx) => (
                <a
                  key={rec.storage_path || idx}
                  href={rec.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`conference-recording-${m.id}-${idx}`}
                  className="flex items-center gap-2 text-white/80 hover:text-white text-xs bg-white/5 hover:bg-white/10 rounded-md px-2 py-1.5 transition"
                >
                  <PlayCircle className="w-3.5 h-3.5 text-tv-accent shrink-0" />
                  <span className="truncate flex-1">{rec.title || "Recording"}</span>
                  <span className="text-white/40 text-[10px]">{formatSize(rec.size)}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function formatSize(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
